import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { environment } from '@environments/environment';

import { Credentials } from '@app/core/models/user/user.model';
import { AuthTokenService } from '../auth-token/auth-token.service';
import {
  baseHttpRequestOptions,
  userMessageErrorHandler,
} from '../services.utils';
import { map, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { FeedbackDialogComponent } from '@app/theme/feedback-dialog/feedback-dialog.component';
import { MatDialog } from '@angular/material/dialog';

const APP_PREFIX = 'SABEMI';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  public LOCAL_STORAGE_USER_JWT_CPF_FIRST_ACCESS =
    APP_PREFIX + '_' + 'CURRENT_USER_CPF_FIRST_ACCESS';

  API_LOGIN_URL: string = environment.apiURL + '/login';

  API_ASSISTED_MODE_LOGIN_URL: string = environment.apiURL + '/login/assisted_mode';

  API_FIRST_ACCESS_LOGIN_URL: string = this.API_LOGIN_URL + '/firstaccess';

  API_USER_EXISTS_URL: string = this.API_LOGIN_URL + '/userexists';

  API_USER_SECURITY_QUESTIONS_URL: string = this.API_LOGIN_URL + '/questions';

  API_USER_PHONE_URL: string = this.API_LOGIN_URL + '/phone';

  API_USER_PASSWORD_URL: string = this.API_LOGIN_URL + '/createPassword';

  API_FORGET_PASSWORD: string = this.API_LOGIN_URL + '/forgetpassword';

  API_UPDATE_PASSWORD: string = this.API_LOGIN_URL + '/updatepassword';

  API_UPDATE_PASSWORD_AUTH: string = this.API_LOGIN_URL + '/updatepasswordauth';

  API_BANNERS: string = this.API_LOGIN_URL + '/banners';

  constructor(
    private readonly httpClient: HttpClient,
    private readonly authTokenService: AuthTokenService,
    public dialog: MatDialog
  ) {}

  signIn({ cpf, password, beTips, beTerms, beOffer }: Credentials) {
    return this.httpClient.post<any>(
      this.API_LOGIN_URL,
      {
        cpf,
        password,
        grantType: 'password',
        beTips,
        beTerms,
        beOffer,
      },
      baseHttpRequestOptions
    );
  }

  assistedModeSignIn({ cpf, password }: Credentials) {
    return this.httpClient.post<any>(
      this.API_ASSISTED_MODE_LOGIN_URL,
      {
        cpf,
        password,
        grantType: 'password',
      },
      baseHttpRequestOptions
    );
  }

  assistedModeCpfClientSignIn({ cpf }: Credentials) {
    return this.httpClient.post<any>(
      this.API_ASSISTED_MODE_LOGIN_URL + '/' + cpf,
      baseHttpRequestOptions
    );
  }

  refreshAccessToken() {
    const { nameid: cpf } = this.authTokenService.decodeAccessToken();

    return this.httpClient.post<any>(
      this.API_LOGIN_URL,
      {
        CPF: cpf,
        refreshToken: this.authTokenService.getToken(
          this.authTokenService.LOCAL_STORAGE_USER_JWT_REFRESH_TOKEN_KEY
        ),
        grantType: 'refresh_token',
      },
      baseHttpRequestOptions
    );
  }

  firstAccessSignIn({ cpf }: Credentials) {
    return this.httpClient.post<any>(
      this.API_FIRST_ACCESS_LOGIN_URL + '/' + cpf,
      baseHttpRequestOptions
    );
  }

  verifyFirstAccess(cpf) {
    return this.httpClient
      .post<any>(this.API_FIRST_ACCESS_LOGIN_URL + '/' + cpf, [], baseHttpRequestOptions)
      .pipe(
        map(({ data: response }) => {
          console.log(response.message);
        }),
        catchError((response) => {
          const message = userMessageErrorHandler(response) || 'Erro ao validar CPF.';
          this.openFeedbackDialog('error', message);
          return throwError(message);
        })
      );
  }

  userExists(cpf) {
    return this.httpClient.post<any>(
      this.API_USER_EXISTS_URL + '/' + cpf,
      baseHttpRequestOptions
    );
  }

  getSecurityQuestions() {
    return this.httpClient
      .get<any>(this.API_USER_SECURITY_QUESTIONS_URL, baseHttpRequestOptions)
      .pipe(map(({ data: securityQuestions }) => securityQuestions));
  }

  sendSecurityQuestionsAnswers(answers) {
    return this.httpClient
      .post<any>(this.API_USER_SECURITY_QUESTIONS_URL, answers, baseHttpRequestOptions)
      .pipe(map(({ data: securityQuestions }) => securityQuestions));
  }

  getPhoneNumber() {
    return this.httpClient.get<any>(this.API_USER_PHONE_URL, baseHttpRequestOptions).pipe(
      map(({ data: { cellPhone: phoneNumber } }) => phoneNumber),
      catchError((response) => {
        const message = userMessageErrorHandler(response) || 'Ocorreu um erro.';
        this.openFeedbackDialog('error', message);
        return throwError(message);
      })
    );
  }

  requestPhoneNumberConfirmationCode() {
    return this.httpClient
      .post<any>(this.API_USER_PHONE_URL, baseHttpRequestOptions)
      .pipe(
        map((data: any) => {
          this.openFeedbackDialog('success', 'Sucesso! O código de confirmação enviado!');
          return data;
        }),
        catchError((response) => {
          const message = userMessageErrorHandler(response) || 'Ocorreu um erro.';
          this.openFeedbackDialog('error', message);
          return throwError(message);
        })
      );
  }

  sendPhoneNumberConfirmationCode(confirmationCode: string) {
    return this.httpClient
      .post<any>(this.API_USER_PHONE_URL + '/' + confirmationCode, baseHttpRequestOptions)
      .pipe(
        map((data: any) => {
          return data;
        }),
        catchError((response) => {
          const message = userMessageErrorHandler(response) || 'Ocorreu um erro.';
          this.openFeedbackDialog('error', message);
          return throwError(message);
        })
      );
  }

  createPassword(passwordData) {
    return this.httpClient
      .post<any>(this.API_USER_PASSWORD_URL, passwordData, baseHttpRequestOptions)
      .pipe(
        map(() => {
          this.deleteCpfUserFirstAccess();
          this.openFeedbackDialog('success', 'Sucesso! Sua senha foi configurada.');
        }),
        catchError((response) => {
          const message = userMessageErrorHandler(response) || 'Ocorreu um erro.';
          this.openFeedbackDialog('error', message);
          return throwError(message);
        })
      );
  }

  forgetpassword(cpfData) {
    const mensagemFeedback = `
      <p>
        Que bom vê-lo por aqui!  <br><br>
      </p>
      <p>
      Para seguir com o processo de alteração da senha o seu CPF precisa estar ativo no Portal do Cliente Sabemi. Mas fique tranquilo, pois a ativação é rápida e fácil!<br><br>
      </p>
      <p>
      1º Passo: acesse portalcliente.sabemi.com.br/login.<br>
      2º Passo: clique em “Ative aqui a sua conta” e informe o seu CPF. Seu nome será identificado e serão realizadas algumas perguntas para confirmarmos sua identidade.<br>
      3º Passo: enviaremos um código de verificação, via SMS, para o seu celular. Basta informar o código na tela do Portal e clicar em avançar! <br>
      4º Passo: PRONTO! Agora é só criar a sua senha e o seu cadastro será finalizado.<br><br>
      </p>
      <p>
        Ter um seguro é ter sempre com quem contar. Conte com a Sabemi!
      </p>`;

    return this.httpClient
      .post<any>(this.API_FORGET_PASSWORD + '/' + cpfData.cpf, [], baseHttpRequestOptions)
      .pipe(
        map(() => {
          // MODAL OK, MODAL API
          this.openFeedbackDialog('warning', mensagemFeedback);
        }),
        catchError((response) => {
          this.openFeedbackDialog('warning', mensagemFeedback);
          const message = userMessageErrorHandler(response) || 'Ocorreu um erro.';
          return throwError(message);
        })
      );
  }
  updatepassword(updatePassData) {
    return this.httpClient
      .post<any>(this.API_UPDATE_PASSWORD, updatePassData, baseHttpRequestOptions)
      .pipe(
        map(() => {
          // MODAL OK, MODAL API
          this.openFeedbackDialog('success', 'Sua senha foi atualizada com sucesso.');
        }),
        catchError(({ error: { errors } }) => {
          // MODAL OK?, MODAL API
          this.openFeedbackDialog('error', errors[0] ? errors[0] : 'Ocorreu um erro');
          return throwError(errors[0]);
        })
      );
  }

  updatepasswordauth(updatePassData) {
    return this.httpClient
      .post<any>(this.API_UPDATE_PASSWORD_AUTH, updatePassData, baseHttpRequestOptions)
      .pipe(
        map(() => {
          // MODAL OK, MODAL API
          this.openFeedbackDialog('success', 'Sua senha foi atualizada com sucesso.');
        }),
        catchError((response) => {
          const message = userMessageErrorHandler(response) || 'Ocorreu um erro.';
          this.openFeedbackDialog('error', message);
          return throwError(message);
        })
      );
  }

  banners() {
    return this.httpClient.post<any>(this.API_BANNERS, {}, baseHttpRequestOptions).pipe(
      map((response: any) => {
        return response.data.data;
      }),
      catchError((response) => {
        const message = userMessageErrorHandler(response) || 'Ocorreu um erro.';
        this.openFeedbackDialog('error', message);
        return throwError(message);
      })
    );
  }

  public setCpfUserFirstAccess(cpf: string): void {
    localStorage.setItem(this.LOCAL_STORAGE_USER_JWT_CPF_FIRST_ACCESS, cpf);
  }

  public getCpfUserFirstAccess(
    cpf = this.LOCAL_STORAGE_USER_JWT_CPF_FIRST_ACCESS
  ): string {
    return localStorage.getItem(cpf);
  }

  public deleteCpfUserFirstAccess(): void {
    localStorage.removeItem(this.LOCAL_STORAGE_USER_JWT_CPF_FIRST_ACCESS);
  }

  logOut(): void {
    this.authTokenService.deleteAccessTokens();
  }

  openFeedbackDialog(color, message) {
    this.openDialog(FeedbackDialogComponent, {
      data: {
        color: color,
        message: message,
      },
    });
  }

  openDialog(dialogComponent, data = {}) {
    return this.dialog.open(dialogComponent, {
      data,
      panelClass: 'feedback-modalbox',
      disableClose: true,
    });
  }
}
